import { useEffect, useMemo, useState } from 'react';
import copy from 'copy-to-clipboard';
import type { PopoverProps } from '@mui/material';
import { Typography, useMediaQuery } from '@mui/material';

import { ETHERSCAN_URL, MINTSCAN_URL } from 'src/constants/explorer';
import { heightDevice } from 'src/constants/muiSize';
import { DYDX_TOKEN, DYDX_TOKEN_ADDRESS, USDC_TOKEN, WETH_DYDX_TOKEN_ADDRESS } from 'src/constants/tokens';
import { WALLET_TYPE } from 'src/constants/wallets';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useDelegationSWR } from 'src/hooks/SWR/cosmos/useDelegationSWR';
import { useRewardsSWR } from 'src/hooks/SWR/cosmos/useRewardsSWR';
import { useUnbondingSWR } from 'src/hooks/SWR/cosmos/useUnbondingSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useBalanceSWR } from 'src/hooks/useBalanceSWR';
import { useCurrentWallet } from 'src/hooks/useCurrentWallet';
import { useTokenBalanceSWR } from 'src/hooks/useTokenBalanceSWR';
import { ExtensionDefault } from 'src/recoils/extension';
import { plus, toDisplayDenomAmount } from 'src/utils/number';
import { shortAddress } from 'src/utils/string';

import {
  BalanceContainer,
  Base02TextContainer,
  BodyAddressContainer,
  BodyContainer,
  BodyItemContainer,
  BodyItemLeftContainer,
  BodyItemTopContainer,
  BodyItemTopIconContainer,
  BodyItemTopLeftContainer,
  BodyItemTopRightContainer,
  BodyTokenBalanceContainer,
  BodyTokenContainer,
  BottomContainer,
  Container,
  Divider,
  IconContainer,
  ImageContainer,
  StyledIconButton,
  StyledPopover,
  StyledReverseGradientButton,
  StyledTooltip,
  TokenContainer,
  TopContainer,
  TopLeftContainer,
  URLButton,
  WalletDisconnectButton,
} from './styled';
import DefaultNumber from '../common/DefaultNumber';
import IconButton from '../IconButton';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import { ReactComponent as CopyIcon } from 'src/assets/svg/Copy.svg';
import { ReactComponent as CosmostationWalletIcon } from 'src/assets/svg/CosmostationWallet.svg';
import { ReactComponent as DYDXChainIcon } from 'src/assets/svg/DYDX.svg';
import { ReactComponent as DydxChainIcon } from 'src/assets/svg/DYDXChain.svg';
import { ReactComponent as ETHDYDXIcon } from 'src/assets/svg/EthDYDX.svg';
import { ReactComponent as EthereumIcon } from 'src/assets/svg/Ethereum.svg';
import { ReactComponent as EthereumChainIcon } from 'src/assets/svg/EthereumIcon.svg';
import { ReactComponent as MetamaskWalletIcon } from 'src/assets/svg/Metamask.svg';
import { ReactComponent as USDCIcon } from 'src/assets/svg/USDC.svg';
import { ReactComponent as WalletDisconnectIcon } from 'src/assets/svg/WalletDisconnect.svg';
import { ReactComponent as WethDYDXIcon } from 'src/assets/svg/WethDYDX.svg';

type WalletPopoverProps = Omit<PopoverProps, 'children'> & {
  onClickDisconnect?: () => void;
  onClickMnemonic: (isOpenPopvover: boolean) => void;
  onClickMobileMnemonic: (isOpenPopvover: boolean) => void;
};

export default function WalletPopover({ onClose, onClickDisconnect, onClickMnemonic, onClickMobileMnemonic, ...remainder }: WalletPopoverProps) {
  const { currentAccount } = useCurrentAccount();
  const { currentWallet, setCurrentWallet } = useCurrentWallet();

  const [isOpenTooltip, setIsOpenTooltip] = useState(false);
  const [isOpenCosmosTooltip, setIsOpenCosmosTooltip] = useState(false);

  const ethBalance = useBalanceSWR({ address: currentAccount.accAddress });
  const cosmosBalance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const delegation = useDelegationSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const rewards = useRewardsSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const unbondings = useUnbondingSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const dydxNativeCoinAmount = useMemo(
    () => cosmosBalance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0',
    [cosmosBalance.data?.balances],
  );

  const usdcCoinAmount = useMemo(
    () => cosmosBalance.data?.balances?.find((item) => item.denom === USDC_TOKEN.denom)?.amount || '0',
    [cosmosBalance.data?.balances],
  );

  const shortEthAddress = useMemo(() => shortAddress(currentAccount.accAddress, 7), [currentAccount.accAddress]);
  const shortCosmosAddress = useMemo(() => shortAddress(currentAccount.cosmosAddress, 7), [currentAccount.cosmosAddress]);

  const displayEthAmount = useMemo(() => toDisplayDenomAmount(BigInt(ethBalance.data || '0').toString(10), 18), [ethBalance]);
  const displayCosmosDYDXAmount = useMemo(() => toDisplayDenomAmount(dydxNativeCoinAmount, DYDX_TOKEN.decimals), [dydxNativeCoinAmount]);
  const displayCosmosUSDCAmount = useMemo(() => toDisplayDenomAmount(usdcCoinAmount, USDC_TOKEN.decimals), [usdcCoinAmount]);

  const totalDelegationBaseAmount = useMemo(
    () =>
      delegation?.data?.delegation_responses
        ?.filter((item) => item.balance.denom === DYDX_TOKEN.denom)
        ?.reduce((ac, cu) => plus(ac, cu.balance.amount), '0')
        .toString() || '0',
    [delegation?.data],
  );

  const totalRewardDYDXBaseAmount = useMemo(() => rewards.data?.total?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [rewards.data?.total]);
  const totalRewardUSDCBaseAmount = useMemo(() => rewards.data?.total?.find((item) => item.denom === USDC_TOKEN.denom)?.amount || '0', [rewards.data?.total]);

  const totalUnbondingDYDXBaseAmount = useMemo(
    () =>
      unbondings?.data?.unbonding_responses
        ?.reduce(
          (ac, cu) =>
            plus(
              ac,
              cu.entries.reduce((a, b) => plus(a, b.balance), '0'),
            ),
          '0',
        )
        .toString() || '0',
    [unbondings?.data],
  );

  const displayTotalDelegationAmount = useMemo(() => toDisplayDenomAmount(totalDelegationBaseAmount, DYDX_TOKEN.decimals), [totalDelegationBaseAmount]);

  const displayTotalRewardDYDXAmount = useMemo(() => toDisplayDenomAmount(totalRewardDYDXBaseAmount, DYDX_TOKEN.decimals), [totalRewardDYDXBaseAmount]);
  const displayTotalRewardUSDCAmount = useMemo(() => toDisplayDenomAmount(totalRewardUSDCBaseAmount, USDC_TOKEN.decimals), [totalRewardUSDCBaseAmount]);

  const displayTotalUnbondingDYDXAmount = useMemo(
    () => toDisplayDenomAmount(totalUnbondingDYDXBaseAmount, DYDX_TOKEN.decimals),
    [totalUnbondingDYDXBaseAmount],
  );

  const sumDYDXAmount = useMemo(
    () => plus(plus(plus(totalDelegationBaseAmount, totalRewardDYDXBaseAmount), totalUnbondingDYDXBaseAmount), dydxNativeCoinAmount),
    [dydxNativeCoinAmount, totalDelegationBaseAmount, totalRewardDYDXBaseAmount, totalUnbondingDYDXBaseAmount],
  );

  const sumUSDCAmount = useMemo(() => plus(totalRewardUSDCBaseAmount, usdcCoinAmount), [totalRewardUSDCBaseAmount, usdcCoinAmount]);

  const displaySumDYDXAmount = useMemo(() => toDisplayDenomAmount(sumDYDXAmount, DYDX_TOKEN.decimals), [sumDYDXAmount]);
  const displaySumUSDCAmount = useMemo(() => toDisplayDenomAmount(sumUSDCAmount, USDC_TOKEN.decimals), [sumUSDCAmount]);

  const tokenBalance = useTokenBalanceSWR({
    address: currentAccount.accAddress,
    tokenContractAddress: DYDX_TOKEN_ADDRESS,
  });

  const wethTokenBalance = useTokenBalanceSWR({
    address: currentAccount.accAddress,
    tokenContractAddress: WETH_DYDX_TOKEN_ADDRESS,
  });

  const displayAmount = useMemo(() => toDisplayDenomAmount(BigInt(tokenBalance.data || '0').toString(10), 18), [tokenBalance.data]);

  const displayWethAmount = useMemo(() => toDisplayDenomAmount(BigInt(wethTokenBalance.data || '0').toString(10), 18), [wethTokenBalance.data]);

  const walletIcon = useMemo(() => {
    if (currentWallet?.walletType === WALLET_TYPE.METAMASK) {
      return <MetamaskWalletIcon />;
    }
    if (currentWallet?.walletType === WALLET_TYPE.COSMOSTATION) {
      return <CosmostationWalletIcon />;
    }
    return undefined;
  }, [currentWallet?.walletType]);

  const handleOnClickCosmosCopy = () => {
    if (copy(currentAccount.cosmosAddress)) {
      setIsOpenCosmosTooltip(true);
    }
  };

  const handleOnClickCopy = () => {
    if (copy(currentAccount.accAddress)) {
      setIsOpenTooltip(true);
    }
  };

  useEffect(() => {
    if (isOpenTooltip) {
      setTimeout(() => {
        setIsOpenTooltip(false);
      }, 1000);
    }
  }, [isOpenTooltip]);

  useEffect(() => {
    if (isOpenCosmosTooltip) {
      setTimeout(() => {
        setIsOpenCosmosTooltip(false);
      }, 1000);
    }
  }, [isOpenCosmosTooltip]);

  const isLaptop = useMediaQuery(heightDevice.laptop);

  return (
    <StyledPopover onClose={onClose} {...remainder}>
      <Container>
        <TopContainer>
          <TopLeftContainer>
            <IconContainer>{walletIcon}</IconContainer>
            <Typography variant="h4">{currentWallet.name}</Typography>
            <WalletDisconnectButton
              onClick={() => {
                onClickDisconnect?.();
                onClose?.({}, 'backdropClick');
                setCurrentWallet(ExtensionDefault);
              }}
            >
              <WalletDisconnectIcon />
            </WalletDisconnectButton>
          </TopLeftContainer>
          <IconButton
            onClick={() => {
              onClose?.({}, 'backdropClick');
            }}
          >
            <IconContainer>
              <CloseIcon />
            </IconContainer>
          </IconButton>
        </TopContainer>
        <BodyContainer data-is-small-device={isLaptop}>
          <BodyItemContainer>
            <BodyItemTopContainer>
              <BodyItemTopLeftContainer>
                <BodyItemTopIconContainer>
                  <EthereumChainIcon />
                </BodyItemTopIconContainer>

                <Typography variant="h6">Ethereum</Typography>
              </BodyItemTopLeftContainer>

              <BodyItemTopRightContainer>
                <URLButton type="button" onClick={() => window.open(`${ETHERSCAN_URL}/address/${currentAccount.accAddress}` || '' || '')}>
                  <BodyAddressContainer>
                    <Typography variant="h9">{shortEthAddress}</Typography>
                  </BodyAddressContainer>
                </URLButton>

                <StyledTooltip title="Copied!" placement="top" arrow open={isOpenTooltip}>
                  <StyledIconButton onClick={handleOnClickCopy}>
                    <CopyIcon />
                  </StyledIconButton>
                </StyledTooltip>
              </BodyItemTopRightContainer>
            </BodyItemTopContainer>

            <TokenContainer>
              <BodyTokenBalanceContainer>
                <BodyItemLeftContainer>
                  <ImageContainer>
                    <ETHDYDXIcon />
                  </ImageContainer>
                  <Typography variant="h9">ethDYDX</Typography>
                </BodyItemLeftContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h6" typoOfDecimals="h9" fixed={6}>
                    {displayAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>

              <BodyTokenBalanceContainer>
                <BodyItemLeftContainer>
                  <ImageContainer>
                    <WethDYDXIcon />
                  </ImageContainer>
                  <Typography variant="h9">wethDYDX</Typography>
                </BodyItemLeftContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h6" typoOfDecimals="h9" fixed={6}>
                    {displayWethAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>

              <BodyTokenBalanceContainer>
                <BodyItemLeftContainer>
                  <ImageContainer>
                    <EthereumIcon />
                  </ImageContainer>
                  <Typography variant="h9">ETH</Typography>
                </BodyItemLeftContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h6" typoOfDecimals="h9" fixed={6}>
                    {displayEthAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>
            </TokenContainer>
          </BodyItemContainer>

          <BodyItemContainer>
            <BodyItemTopContainer>
              <BodyItemTopLeftContainer>
                <BodyItemTopIconContainer>
                  <DydxChainIcon />
                </BodyItemTopIconContainer>

                <Typography variant="h6">dYdX Chain</Typography>
              </BodyItemTopLeftContainer>

              <BodyItemTopRightContainer>
                <URLButton type="button" onClick={() => window.open(`${MINTSCAN_URL}/address/${currentAccount.cosmosAddress}` || '')}>
                  <BodyAddressContainer>
                    <Typography variant="h9">{shortCosmosAddress}</Typography>
                  </BodyAddressContainer>
                </URLButton>

                <StyledTooltip title="Copied!" placement="top" arrow open={isOpenCosmosTooltip}>
                  <StyledIconButton onClick={handleOnClickCosmosCopy}>
                    <CopyIcon />
                  </StyledIconButton>
                </StyledTooltip>
              </BodyItemTopRightContainer>
            </BodyItemTopContainer>

            <TokenContainer>
              <BodyTokenContainer>
                <BodyTokenBalanceContainer>
                  <BodyItemLeftContainer>
                    <ImageContainer>
                      <DYDXChainIcon />
                    </ImageContainer>
                    <Typography variant="h9">DYDX</Typography>
                  </BodyItemLeftContainer>

                  <BalanceContainer>
                    <DefaultNumber typoOfIntegers="h6" typoOfDecimals="h9" fixed={6}>
                      {displaySumDYDXAmount}
                    </DefaultNumber>
                  </BalanceContainer>
                </BodyTokenBalanceContainer>
              </BodyTokenContainer>

              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Available</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11" fixed={6}>
                    {displayCosmosDYDXAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>

              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Staked</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11" fixed={6}>
                    {displayTotalDelegationAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>
              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Unstaking</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11" fixed={6}>
                    {displayTotalUnbondingDYDXAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>

              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Reward</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11">
                    {displayTotalRewardDYDXAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>
            </TokenContainer>

            <Divider />

            <TokenContainer>
              <BodyTokenContainer>
                <BodyTokenBalanceContainer>
                  <BodyItemLeftContainer>
                    <ImageContainer>
                      <USDCIcon />
                    </ImageContainer>
                    <Typography variant="h9">USDC</Typography>
                  </BodyItemLeftContainer>

                  <BalanceContainer>
                    <DefaultNumber typoOfIntegers="h6" typoOfDecimals="h9" fixed={6}>
                      {displaySumUSDCAmount}
                    </DefaultNumber>
                  </BalanceContainer>
                </BodyTokenBalanceContainer>
              </BodyTokenContainer>

              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Available</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11" fixed={6}>
                    {displayCosmosUSDCAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>

              <BodyTokenBalanceContainer>
                <Base02TextContainer>
                  <Typography variant="h11">Reward</Typography>
                </Base02TextContainer>

                <BalanceContainer>
                  <DefaultNumber typoOfIntegers="h8" typoOfDecimals="h11">
                    {displayTotalRewardUSDCAmount}
                  </DefaultNumber>
                </BalanceContainer>
              </BodyTokenBalanceContainer>
            </TokenContainer>

            <BottomContainer>
              <StyledReverseGradientButton
                typoVarient="h8"
                onClick={() => {
                  onClickMnemonic(true);
                  onClose?.({}, 'backdropClick');
                }}
              >
                Export Mnemonic
              </StyledReverseGradientButton>

              <StyledReverseGradientButton
                typoVarient="h8"
                onClick={() => {
                  onClickMobileMnemonic(true);
                  onClose?.({}, 'backdropClick');
                }}
              >
                Export Mnemonic QR
              </StyledReverseGradientButton>
            </BottomContainer>
          </BodyItemContainer>
        </BodyContainer>
      </Container>
    </StyledPopover>
  );
}
